import { Injectable, OnDestroy } from '@angular/core';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { SocketService } from './socket.service';
import { Subject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IEventNotification } from '@core/interfaces/socket.interface';
import { environment } from '@env/environment';
import { logger } from '@shared/utils/functions.utils';
import { Socket } from 'socket.io-client';

/**
 * Servicio profesional para manejar eventos de comandas vía WebSocket
 * Implementa OnDestroy para limpieza automática de recursos
 */
@Injectable({
  providedIn: 'root',
})
export class ComandaListenerService implements OnDestroy {
  constructor(private socketService: SocketService) {}
  private readonly namespace = '/comandas';

  // Subject privado (no exponer públicamente)
  private readonly eventNotificationSubject = new Subject<IEventNotification>();

  // Observable público readonly (inmutable desde fuera)
  readonly events$: Observable<IEventNotification> = this.eventNotificationSubject.asObservable();

  /**
   * Filtra eventos por tipo específico
   * @param eventType Tipo de evento a filtrar
   */
  onEvent<T = unknown>(eventType: EVENT_GATEWAY): Observable<IEventNotification<T>> {
    return this.events$.pipe(
      filter((notification) => notification.event === eventType),
    ) as Observable<IEventNotification<T>>;
  }

  /**
   * Obtiene solo los datos del evento, sin metadata
   * @param eventType Tipo de evento
   */
  getData<T = unknown>(eventType: EVENT_GATEWAY): Observable<T> {
    return this.onEvent<T>(eventType).pipe(map((notification) => notification.data));
  }

  /**
   * Filtra eventos por sucursal
   * @param sucursalId ID de la sucursal
   */
  onSucursal(sucursalId: number): Observable<IEventNotification> {
    return this.events$.pipe(filter((notification) => notification.sucursalId === sucursalId));
  }

  /**
   * Detiene el listener y limpia recursos
   */
  stop(sucursal_id: number): void {
    const socket = this.socketService.obtenerSocket(sucursal_id, this.namespace);
    if (!socket) return;

    // Remover listeners para evitar fugas de memoria
    this.limpiarListeners(socket);

    this.socketService.desconectarSocketEspecifico(sucursal_id, this.namespace);
  }

  /**
   * Inicia el listener para una sucursal
   */
  start(sucursal_id: number): void {
    const socket = this.socketService.conectarSucursal(sucursal_id, this.namespace);
    // Log adicional de debug (solo en desarrollo)
    if (!environment.production) {
      logger('[ComandaListener] Estado:', {
        connected: socket?.connected,
        namespace: this.namespace,
        id: socket?.id,
      });
    }

    if (!socket) {
      console.error('[ComandaListener] No se pudo obtener la instancia del socket');
      return;
    }

    if (!environment.production) {
      logger('[ComandaListener] Inicializando listeners...');
    }

    this.limpiarListeners(socket);

    // Evento de conexión exitosa
    socket.on(EVENT_GATEWAY.CONNECT, () => {
      if (!environment.production) {
        logger('[ComandaListener] ✅ Conectado al servidor WebSocket');
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.CONNECT,
        data: null,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Evento de desconexión
    socket.on(EVENT_GATEWAY.DISCONNECT, (reason: string) => {
      logger('[ComandaListener] ❌ Desconectado del servidor:', reason);
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.DISCONNECT,
        data: reason,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Listeners de eventos de comanda
    socket.on(EVENT_GATEWAY.COMANDA_MESA_NUEVA, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] 📋 Nueva mesa con comanda:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_MESA_NUEVA,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_MESA_ACTUALIZADA, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] 📝 Mesa actualizada:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_MESA_ACTUALIZADA,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_MESA_ACTUALIZAR_POSICIONES, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] 📍 Mesa actualizar posiciones:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_MESA_ACTUALIZAR_POSICIONES,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_AGREGAR_ITEMS, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] ➕ Agregar items a la comanda:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_AGREGAR_ITEMS,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_ELIMINAR_ITEM, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] ❌ Eliminar item de la comanda:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_ELIMINAR_ITEM,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_ACTUALIZAR_ESTADO, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] 🔄 Actualizar estado de productos en la comanda:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_ACTUALIZAR_ESTADO,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_MESA_LIMPIADA, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] 🧹 Mesa limpiada:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_MESA_LIMPIADA,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_NUEVA, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] 🆕 Nueva comanda:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_NUEVA,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.COMANDA_ACTUALIZADA, (data) => {
      if (!environment.production) {
        logger('[ComandaListener] ✏️ Comanda actualizada:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.COMANDA_ACTUALIZADA,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Log del estado de conexión inicial (solo en desarrollo)
    if (!environment.production) {
      logger(
        `[ComandaListener] Estado inicial: ${socket.connected ? '✅ Conectado' : '⏳ Conectando...'}`,
      );
    }
  }

  /**
   * Limpieza automática cuando el servicio es destruido
   */
  ngOnDestroy(): void {
    this.eventNotificationSubject.complete();
  }

  private limpiarListeners(socket: Socket) {
    // Limpiar listeners previos para evitar duplicados si start() se llama más de una vez
    socket.off(EVENT_GATEWAY.CONNECT);
    socket.off(EVENT_GATEWAY.DISCONNECT);
    socket.off(EVENT_GATEWAY.COMANDA_MESA_NUEVA);
    socket.off(EVENT_GATEWAY.COMANDA_MESA_ACTUALIZADA);
    socket.off(EVENT_GATEWAY.COMANDA_MESA_ACTUALIZAR_POSICIONES);
    socket.off(EVENT_GATEWAY.COMANDA_AGREGAR_ITEMS);
    socket.off(EVENT_GATEWAY.COMANDA_ELIMINAR_ITEM);
    socket.off(EVENT_GATEWAY.COMANDA_ACTUALIZAR_ESTADO);
    socket.off(EVENT_GATEWAY.COMANDA_MESA_LIMPIADA);
    socket.off(EVENT_GATEWAY.COMANDA_NUEVA);
    socket.off(EVENT_GATEWAY.COMANDA_ACTUALIZADA);
  }
}
